import * as React from 'react';
import { getStoreState } from 'state/Store';
import { formatTimestamp } from 'utils';
import { ItemValue } from '../components/SelectRich';
import Layer from '../data/Layer';
import { PropertyTimeline, TimelinePoint } from '../data/LayerTimeline';

interface Props {
  layer: Layer;
  effect: ItemValue;
  onExpand: () => void;
}

interface State {
  draggedEdge: HTMLDivElement | null;
}

export class EffectTimelineSimpleView extends React.Component<Props> {
  containerEl = React.createRef<HTMLDivElement>();
  canvasEl = React.createRef<HTMLCanvasElement>();
  leftEdgeEl = React.createRef<HTMLDivElement>();
  rightEdgeEl = React.createRef<HTMLDivElement>();

  timeTooltipEl = React.createRef<HTMLDivElement>();

  canvasWidth = 280;
  canvasHeight = 28;

  state: State = {
    draggedEdge: null
  };

  componentDidMount() {
    if (this.canvasEl.current && this.containerEl.current) {
      this.canvasWidth = this.containerEl.current.getBoundingClientRect().width;
      this.canvasEl.current.width = this.canvasWidth;
      this.updateCanvas();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.didDrag);
    document.removeEventListener('mouseup', this.didStopDrag);
  }

  updateCanvas() {
    const { layer, effect } = this.props;
    if (!this.canvasEl.current) {
      return;
    }
    const ctx = this.canvasEl.current.getContext('2d')!;
    ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    ctx.fillStyle =
      this.props.effect.id === 'visibility'
        ? 'rgba(0,0,0, 0.2)'
        : 'rgba(235, 49, 151, 0.75)';
    const timeline = layer.timeline[effect.id];
    if (!timeline) {
      ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
    } else {
      ctx.beginPath();
      const points = timeline.points.concat([]);
      points.unshift({
        position: 0,
        value: 0
      });
      points.push({
        position: 1,
        value: 0
      });
      let p = points[0];
      ctx.moveTo(
        p.position * this.canvasWidth,
        (1 - p.value) * this.canvasHeight
      );
      for (let i = 1; i < points.length; i++) {
        p = points[i];
        ctx.lineTo(
          p.position * this.canvasWidth,
          (1 - p.value) * this.canvasHeight
        );
      }
      ctx.fill();
    }
    if (this.leftEdgeEl.current) {
      const begin = this.leftEdgePosition * this.canvasWidth;
      this.leftEdgeEl.current.style.left = begin + 'px';
    }
    if (this.rightEdgeEl.current) {
      const end = this.rightEdgePosition * this.canvasWidth;
      this.rightEdgeEl.current.style.left = end + 'px';
    }
  }

  updateDragTooltip() {
    if (
      this.containerEl.current &&
      this.timeTooltipEl.current &&
      this.state.draggedEdge
    ) {
      const containerRect = this.containerEl.current.getBoundingClientRect();
      let p =
        this.state.draggedEdge === this.leftEdgeEl.current
          ? this.leftEdgePosition
          : this.rightEdgePosition;

      this.timeTooltipEl.current.style.left = `${
        this.state.draggedEdge.getBoundingClientRect().x
      }px`;
      this.timeTooltipEl.current.style.top = `${containerRect.y - 2}px`;
      this.timeTooltipEl.current.style.display = 'block';

      let millis = getStoreState().flyer.motionSource.getDuration() * p;
      this.timeTooltipEl.current.textContent = formatTimestamp(millis);
    }
  }

  get timeline(): PropertyTimeline | null {
    const { layer, effect } = this.props;
    return layer.timeline[effect.id] || null;
  }

  get leftEdgePosition(): number {
    const tl = this.timeline;
    if (!tl) {
      return 0;
    }
    return tl.points.find(p => p.value === 1)!.position;
  }

  get rightEdgePosition(): number {
    const tl = this.timeline;
    if (!tl) {
      return 1;
    }
    return tl.points
      .concat([])
      .reverse()
      .find(p => p.value === 1)!.position;
  }

  canDragBorders(): boolean {
    const tl = this.timeline;
    if (!tl) {
      return true;
    }
    if (!tl.isCustom()) {
      return true;
    }
    // left is flat, right is flat and left and right are next
    const leftPoint = tl.points.find(p => p.value === 1);
    const rightPoint = tl.points
      .concat([])
      .reverse()
      .find(p => p.value === 1);
    if (leftPoint && rightPoint && leftPoint !== rightPoint) {
      const leftIndex = tl.points.indexOf(leftPoint);
      const preLeftPoint = leftIndex > 0 ? tl.points[leftIndex - 1] : null;
      let isLeftFlat =
        preLeftPoint === null ||
        (preLeftPoint.position === leftPoint.position &&
          preLeftPoint.value === 0);
      const rightIndex = tl.points.indexOf(rightPoint);
      const postRightPoint =
        rightIndex < tl.points.length - 1 ? tl.points[rightIndex + 1] : null;
      let isRightFlat =
        postRightPoint === null ||
        (postRightPoint.position === rightPoint.position &&
          postRightPoint.value === 0);
      if (isLeftFlat && isRightFlat && rightIndex === leftIndex + 1) {
        return true;
      }
    }
    return false;
  }

  startDragging = (el: HTMLDivElement) => {
    this.setState({ draggedEdge: el }, () => {
      document.addEventListener('mousemove', this.didDrag);
      document.addEventListener('mouseup', this.didStopDrag);
      this.updateDragTooltip();
    });
  };

  didDrag = (e: MouseEvent) => {
    if (!this.containerEl.current) {
      return;
    }
    let p =
      (e.clientX - this.containerEl.current.getBoundingClientRect().x) /
      this.canvasWidth;
    p = Math.max(0, Math.min(1, p));
    const { layer, effect } = this.props;
    const { draggedEdge } = this.state;
    let tl = this.timeline;
    if (!tl) {
      tl = new PropertyTimeline();
      layer.timeline[effect.id] = tl;
    }
    let left = this.leftEdgePosition;
    let right = this.rightEdgePosition;
    switch (draggedEdge) {
      case this.leftEdgeEl.current:
        left = p;
        break;
      case this.rightEdgeEl.current:
        right = p;
        break;
      default:
        break;
    }
    const points: TimelinePoint[] = [];
    if (left > 0) {
      points.push({
        position: 0,
        value: 0
      });
      points.push({
        position: left,
        value: 0
      });
    }
    points.push({
      position: left,
      value: 1
    });
    points.push({
      position: right,
      value: 1
    });
    if (right < 1) {
      points.push({
        position: right,
        value: 0
      });
      points.push({
        position: 1,
        value: 0
      });
    }
    tl.points = points;
    this.updateCanvas();
    this.updateDragTooltip();
  };

  didStopDrag = () => {
    document.removeEventListener('mousemove', this.didDrag);
    document.removeEventListener('mouseup', this.didStopDrag);
    this.setState({ draggedEdge: null });
  };

  render() {
    return (
      <div ref={this.containerEl} className="effect-simple-timeline">
        <canvas
          ref={this.canvasEl}
          height={this.canvasHeight}
          onClick={() => this.props.onExpand()}
        />
        {this.canDragBorders() && (
          <>
            <div
              ref={this.leftEdgeEl}
              className="edge"
              onMouseDown={() => this.startDragging(this.leftEdgeEl.current!)}
            />
            <div
              ref={this.rightEdgeEl}
              className="edge"
              onMouseDown={() => this.startDragging(this.rightEdgeEl.current!)}
            />
          </>
        )}
        {this.state.draggedEdge !== null && (
          <div ref={this.timeTooltipEl} className="drag-time">
            00:00:00
          </div>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/controller/EffectTimelineSimpleView.tsx